import {
  Badge,
  Icon,
  IconType,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@sellar-io/components";
import dayjs from "dayjs";
import { twMerge } from "tailwind-merge";
import { SupplierOnly } from "~/components";
import { CutoffCountdown } from "~/pages/discovery/v2/distribution/CutoffCountdown/CutoffCountdown";
import {
  CutoffCountdownDuration,
  useCutoffCountdown,
} from "~/pages/discovery/v2/distribution/CutoffCountdown/useCutoffCountdown";

type SellarDistributionSupplierCutOffAlertContainerOrder = {
  id: number;
  confirmedAt: Date | null;
  cancelledAt: Date | null;
  fulfillmentType: string;
  supplierFinalisationCutOffAt: Date | null;
};

type SellarDistributionSupplierCutOffAlertContainerProps = {
  mode: "badge" | "alert";
  order: SellarDistributionSupplierCutOffAlertContainerOrder;
};

export const SellarDistributionSupplierCutOffAlertContainer = ({
  mode,
  order,
}: SellarDistributionSupplierCutOffAlertContainerProps) => {
  const cutOffDate = order.supplierFinalisationCutOffAt;
  const timeLeft = useCutoffCountdown(cutOffDate);
  const variant = cutOffDate ? getVariant({ timeLeft }) : null;

  if (!cutOffDate || !variant) {
    return null;
  }

  const orderIsEligibleForCutOffAlert =
    !!order.id &&
    !order.confirmedAt &&
    !order.cancelledAt &&
    order.fulfillmentType === "sellarDistribution";

  if (!orderIsEligibleForCutOffAlert) {
    return null;
  }

  return (
    <SupplierOnly>
      {mode === "alert" && (
        <SellarDistributionSupplierCutOffAlert
          variant={variant}
          cutOffDate={cutOffDate}
          timeLeft={timeLeft}
        />
      )}
      {mode === "badge" && (
        <SellarDistributionSupplierCutOffBadge
          variant={variant}
          cutOffDate={cutOffDate}
          timeLeft={timeLeft}
        />
      )}
    </SupplierOnly>
  );
};

type CutOffVariant = "informative" | "urgent" | "expired";

type SellarDistributionSupplierCutOffProps = {
  cutOffDate: Date;
  timeLeft: CutoffCountdownDuration;
  variant: CutOffVariant;
};

const SellarDistributionSupplierCutOffAlert = ({
  cutOffDate,
  timeLeft,
  variant,
}: SellarDistributionSupplierCutOffProps) => {
  const { icon, message, cutOffTitleText } = getVariantData({
    variant,
    cutOffDate,
  });

  return (
    <div
      className={twMerge(
        "flex items-center gap-4 rounded-lg border px-3 py-2 text-zinc-800",
        variant === "informative" && "border-blue-300 bg-blue-100",
        variant === "urgent" && "border-orange-300 bg-orange-100",
        variant === "expired" && "border-zinc-300 bg-zinc-100",
      )}
    >
      <div className="flex flex-1 items-center gap-3">
        <Icon
          icon={icon}
          size={0.7}
          className={twMerge(
            variant === "informative" && "text-blue-800",
            variant === "urgent" && "text-orange-800",
            variant === "expired" && "text-zinc-800",
          )}
        />
        <p className="flex-1 text-sm font-semibold">{message}</p>
      </div>
      <div
        className={twMerge(
          "hidden items-center gap-2 rounded-lg px-3 py-2 sm:flex",
          variant === "informative" && "bg-blue-200 text-blue-800",
          variant === "urgent" && "bg-orange-200 text-orange-800",
          variant === "expired" && "bg-zinc-200 text-zinc-800",
        )}
      >
        <Icon icon="CLOCK_OUTLINE" size={0.6} />
        <div className="flex flex-col text-xs">
          <p>{cutOffTitleText}</p>
          <CutoffCountdown
            timeLeft={timeLeft}
            format={
              dayjs().isBefore(dayjs(cutOffDate), "day")
                ? "D[d] H[h] mm[m] ss[s]"
                : "H[h] mm[m] ss[s]"
            }
            className={twMerge(
              "font-semibold",
              variant === "expired" && "line-through",
            )}
          />
        </div>
      </div>
    </div>
  );
};

const SellarDistributionSupplierCutOffBadge = ({
  cutOffDate,
  timeLeft,
  variant,
}: SellarDistributionSupplierCutOffProps) => {
  const { icon, message, cutOffTitleText } = getVariantData({
    variant,
    cutOffDate,
  });

  return (
    <div>
      <Tooltip placement="bottom">
        <TooltipTrigger
          className="w-full"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <Badge
            className={twMerge(
              "w-fit text-sm",
              variant === "informative" &&
                "border-blue-300 bg-blue-100 text-blue-800",
              variant === "urgent" &&
                "border-orange-300 bg-orange-100 text-orange-800",
              variant === "expired" &&
                "border-zinc-300 bg-zinc-100 text-zinc-800",
            )}
            icon={variant === "urgent" ? "CLOCK_FAST_OUTLINE" : "CLOCK_OUTLINE"}
          >
            <CutoffCountdown
              timeLeft={timeLeft}
              format={
                dayjs().isBefore(dayjs(cutOffDate), "day")
                  ? "D[d] H[h] mm[m] ss[s]"
                  : "H[h] mm[m] ss[s]"
              }
              className={twMerge(
                "font-semibold",
                variant === "expired" && "line-through",
              )}
            />
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="w-80 rounded-lg border border-zinc-300 bg-white px-4 py-3 shadow-lg">
          <div className="flex items-center gap-2">
            <Icon icon={icon} size={0.75} />
            <div className="flex flex-col gap-1">
              <p className="font-semibold">{cutOffTitleText}</p>
              <p>{message}</p>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

const getVariantData = ({
  variant,
  cutOffDate,
}: {
  variant: CutOffVariant;
  cutOffDate: Date;
}): { icon: IconType; message: string; cutOffTitleText: string } => {
  switch (variant) {
    case "informative":
      return {
        icon: "INFORMATION",
        message:
          "Finalise this order before the expected collection cut-off time so that your customer's delivery expectations are met",
        cutOffTitleText: `Cut off ${dayjs().isBefore(dayjs(cutOffDate), "day") ? dayjs(cutOffDate).format("hh:mma ddd") : dayjs(cutOffDate).format("hh:mma")}`,
      };
    case "urgent":
      return {
        icon: "ALERT",
        message:
          "The cut-off for collection by Sellar Distribution is in the next hour. Finalise this order promptly so that the cut-off isn't missed",
        cutOffTitleText: "Cut off soon!",
      };
    case "expired":
      return {
        icon: "INFORMATION",
        message:
          "The cut-off for your customer's expected delivery date has been missed. Collection and delivery dates will be recalculated on finalisation.",
        cutOffTitleText: "Cut off missed",
      };
    default:
      return {
        icon: "INFORMATION",
        message: "Finalise this order",
        cutOffTitleText: "Cut off",
      };
  }
};

const getVariant = ({
  timeLeft,
}: {
  timeLeft: CutoffCountdownDuration;
}): CutOffVariant => {
  const secondsLeft = timeLeft.asSeconds();

  if (secondsLeft <= 0) {
    return "expired";
  }

  if (secondsLeft < 60 * 60) {
    return "urgent";
  }

  return "informative";
};
